// ============================================================================
// AMBIENT BACKGROUND WIDGET - Reactive neural network canvas
// Nodes = session avatars (coloured) + keyword nodes (from transcript)
// Reacts to: speaking avatar (glow), isAvatarsThinking (speed + pulse),
//            phase transitions (burst), and idle drift
// ============================================================================

import React, { useEffect, useRef, useCallback } from 'react';
import { useSecondScreenData } from '../../../contexts/SecondScreenContext';
import type { WidgetProps } from '../../../types/second-screen.types';

const MAX_KEYWORD_NODES = 12;
const STOP_WORDS = new Set([
  'the','and','for','that','this','with','are','was','were','have','has',
  'had','but','not','from','they','what','can','will','our','your','its',
  'who','how','all','one','out','more','about','into','when','their','been',
  'than','then','also','just','any','each','over','after','some','would',
  'there','which','you','we','is','it','be','do','as','an','a','of','in',
  'to','at','by','or','on','so','if','up','he','she','his','her',
]);

interface CanvasNode {
  x: number;
  y: number;
  vx: number;
  vy: number;
  label: string;
  color: string;
  radius: number;
  baseRadius: number;
  type: 'avatar' | 'keyword';
  role?: string;
}

interface Particle {
  x: number; y: number; vx: number; vy: number;
  alpha: number; color: string; radius: number;
}

function extractKeywords(transcript: any[]): string[] {
  const freq: Record<string, number> = {};
  for (const entry of transcript) {
    const text: string = (entry.text || entry.content || '').toLowerCase();
    const words = text.match(/[a-z]{4,}/g) || [];
    for (const w of words) {
      if (!STOP_WORDS.has(w)) {
        freq[w] = (freq[w] || 0) + 1;
      }
    }
  }
  return Object.entries(freq)
    .sort((a, b) => b[1] - a[1])
    .slice(0, MAX_KEYWORD_NODES)
    .map(([w]) => w);
}

const AmbientBackgroundWidget: React.FC<WidgetProps> = ({ config }) => {
  const { state, theme } = useSecondScreenData();
  const themeRef = useRef(theme);
  themeRef.current = theme;
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const nodesRef = useRef<CanvasNode[]>([]);
  const particlesRef = useRef<Particle[]>([]);
  const rafRef = useRef<number>(0);
  const frameRef = useRef(0);
  const stateRef = useRef(state);
  stateRef.current = state;

  const intensity = config?.intensity || 'moderate';
  const baseAlphaMultiplier = intensity === 'subtle' ? 0.5 : intensity === 'vivid' ? 1.5 : 1.0;

  const initNodes = useCallback((width: number, height: number) => {
    const avatars = stateRef.current?.sessionAvatars || [];
    const transcript = stateRef.current?.transcript || [];
    const keywords = extractKeywords(transcript);

    const nodes: CanvasNode[] = [];

    // Avatar nodes
    for (const av of avatars) {
      nodes.push({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.4,
        vy: (Math.random() - 0.5) * 0.4,
        label: av.name,
        color: av.primaryColor || '#00c7c4',
        radius: 10,
        baseRadius: 10,
        type: 'avatar',
        role: av.role,
      });
    }

    // Keyword nodes
    for (const kw of keywords.slice(0, MAX_KEYWORD_NODES)) {
      nodes.push({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.25,
        vy: (Math.random() - 0.5) * 0.25,
        label: kw,
        color: themeRef.current === 'light' ? 'rgba(30,30,60,0.45)' : 'rgba(255,255,255,0.45)',
        radius: 4,
        baseRadius: 4,
        type: 'keyword',
      });
    }

    nodesRef.current = nodes;
  }, []);

  // Re-extract keywords when transcript grows (every 5 entries)
  const prevTranscriptLenRef = useRef(0);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const len = state?.transcript?.length || 0;
    if (Math.abs(len - prevTranscriptLenRef.current) >= 5) {
      prevTranscriptLenRef.current = len;
      initNodes(canvas.width, canvas.height);
    }
  }, [state?.transcript?.length, initNodes]);

  // Reinit when avatars change
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    initNodes(canvas.width, canvas.height);
  }, [state?.sessionAvatars, initNodes]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resizeObserver = new ResizeObserver(() => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
      initNodes(canvas.width, canvas.height);
    });
    resizeObserver.observe(canvas);

    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;
    initNodes(canvas.width, canvas.height);

    const draw = () => {
      const s = stateRef.current;
      const w = canvas.width;
      const h = canvas.height;
      frameRef.current++;

      const isThinking = s?.isAvatarsThinking || false;
      const speakingRole = s?.speakingAvatar || null;
      const speedMult = isThinking ? 2.4 : speakingRole ? 1.4 : 1.0;

      // Clear with slight trail effect — colour matches the page background theme
      ctx.fillStyle = themeRef.current === 'light'
        ? 'rgba(240, 242, 245, 0.22)'   // light: bg-slate-100 equivalent
        : 'rgba(5, 40, 49, 0.18)';      // dark: navy
      ctx.fillRect(0, 0, w, h);

      const nodes = nodesRef.current;
      const particles = particlesRef.current;

      // Spawn particles from avatar nodes
      if (frameRef.current % 8 === 0) {
        for (const node of nodes) {
          if (node.type !== 'avatar') continue;
          const count = isThinking ? 2 : speakingRole === node.role ? 2 : 1;
          for (let i = 0; i < count; i++) {
            particles.push({
              x: node.x,
              y: node.y,
              vx: (Math.random() - 0.5) * 1.2,
              vy: (Math.random() - 0.5) * 1.2,
              alpha: 0.5 * baseAlphaMultiplier,
              color: node.color,
              radius: Math.random() * 2 + 1,
            });
          }
        }
        // Cap particles
        if (particles.length > 200) {
          particles.splice(0, particles.length - 200);
        }
      }

      // Update + draw particles
      for (let i = particles.length - 1; i >= 0; i--) {
        const p = particles[i];
        p.x += p.vx * speedMult;
        p.y += p.vy * speedMult;
        p.alpha -= 0.012;
        if (p.alpha <= 0) { particles.splice(i, 1); continue; }
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        ctx.fillStyle = p.color.replace(')', `, ${p.alpha})`).replace('rgb', 'rgba').replace('rgba(rgba', 'rgba');
        // Fallback for hex colors
        ctx.globalAlpha = p.alpha * baseAlphaMultiplier;
        ctx.fillStyle = p.color;
        ctx.fill();
        ctx.globalAlpha = 1;
      }

      // Draw edges between nearby nodes
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[i].x - nodes[j].x;
          const dy = nodes[i].y - nodes[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          const maxDist = 200;
          if (dist < maxDist) {
            const alpha = (1 - dist / maxDist) * 0.18 * baseAlphaMultiplier;
            ctx.beginPath();
            ctx.moveTo(nodes[i].x, nodes[i].y);
            ctx.lineTo(nodes[j].x, nodes[j].y);
            ctx.strokeStyle = nodes[i].type === 'avatar' ? nodes[i].color : nodes[j].type === 'avatar' ? nodes[j].color : 'rgba(255,255,255,0.3)';
            ctx.globalAlpha = alpha * (isThinking ? 2 : 1);
            ctx.lineWidth = 1;
            ctx.stroke();
            ctx.globalAlpha = 1;
          }
        }
      }

      // Update + draw nodes
      for (const node of nodes) {
        // Move
        node.x += node.vx * speedMult;
        node.y += node.vy * speedMult;

        // Bounce
        if (node.x < node.radius || node.x > w - node.radius) node.vx *= -1;
        if (node.y < node.radius || node.y > h - node.radius) node.vy *= -1;
        node.x = Math.max(node.radius, Math.min(w - node.radius, node.x));
        node.y = Math.max(node.radius, Math.min(h - node.radius, node.y));

        const isSpeaking = node.role && node.role === speakingRole;

        // Animated radius
        if (node.type === 'avatar') {
          const pulse = isThinking ? Math.sin(Date.now() / 400) * 3 : isSpeaking ? Math.sin(Date.now() / 250) * 4 : 0;
          node.radius = node.baseRadius + pulse;
        }

        // Glow for speaking/thinking avatar nodes
        if (node.type === 'avatar' && (isSpeaking || isThinking)) {
          ctx.shadowColor = node.color;
          ctx.shadowBlur = isSpeaking ? 24 : 12;
        }

        // Draw node
        ctx.beginPath();
        ctx.arc(node.x, node.y, Math.max(1, node.radius), 0, Math.PI * 2);
        ctx.fillStyle = node.color;
        ctx.globalAlpha = node.type === 'keyword' ? 0.4 * baseAlphaMultiplier : 0.85 * baseAlphaMultiplier;
        ctx.fill();
        ctx.globalAlpha = 1;
        ctx.shadowBlur = 0;

        // Label for avatar nodes
        if (node.type === 'avatar') {
          ctx.font = '11px Inter, system-ui, sans-serif';
          ctx.fillStyle = themeRef.current === 'light' ? 'rgba(15,23,42,0.65)' : 'rgba(255,255,255,0.55)';
          ctx.globalAlpha = 0.7;
          ctx.fillText(node.label, node.x + node.radius + 4, node.y + 4);
          ctx.globalAlpha = 1;
        }
      }

      rafRef.current = requestAnimationFrame(draw);
    };

    rafRef.current = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(rafRef.current);
      resizeObserver.disconnect();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <canvas
      ref={canvasRef}
      className="w-full h-full block"
      style={{ background: 'transparent' }}
    />
  );
};

export default AmbientBackgroundWidget;
